/**
 * DownloadService.ts
 * Handles file downloads based on current date and company selection
 */

import type { MonthValue, YearValue, PeriodType, ProfileData } from '../types';
import { FILE_PATHS, TARGET_COMPANIES } from '../utils/constants';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Criteria for determining which file to download (Quick Comps)
 */
export interface DownloadCriteria {
  month: MonthValue;
  year: YearValue;
  visibleCompanies: string[];
}

/**
 * Criteria for determining which file to download (Financial Statements)
 */
export interface FinancialStatementCriteria {
  leftYear: string;
  rightYear: string;
  periodType: PeriodType;
}

/**
 * Service for handling Excel file downloads
 */
export class DownloadService {
  /**
   * Trigger a file download based on current criteria (Quick Comps)
   */
  public downloadFile(criteria: DownloadCriteria): void {
    const filePath = this.determineFilePath(criteria);
    const fileName = this.extractFileName(filePath);

    this.createDownloadLink(filePath, fileName);
  }

  /**
   * Trigger a file download for financial statements based on timeline and period
   */
  public downloadFinancialStatement(criteria: FinancialStatementCriteria): void {
    const filePath = this.determineFinancialStatementPath(criteria);
    const fileName = this.extractFileName(filePath);

    this.createDownloadLink(filePath, fileName);
  }

  /**
   * Determine which file to download based on criteria
   */
  private determineFilePath(criteria: DownloadCriteria): string {
    const { month, year, visibleCompanies } = criteria;

    // Sort for comparison
    const sortedVisible = [...visibleCompanies].sort();
    const sortedTarget = [...TARGET_COMPANIES].sort();

    // Check if visible companies match target companies
    const matchesTargetCompanies = this.arraysEqual(sortedVisible, sortedTarget);

    // Return appropriate file path
    if (month === '03' && year === '2023' && matchesTargetCompanies) {
      return FILE_PATHS.COMPS_2023;
    } else if (month === '03' && year === '2024' && matchesTargetCompanies) {
      return FILE_PATHS.COMPS_2024;
    } else if (month === '03' && year === '2025' && matchesTargetCompanies) {
      return FILE_PATHS.COMPS_2025;
    } else {
      // Default file for other combinations
      return FILE_PATHS.CAPIQ_DEFAULT;
    }
  }

  /**
   * Determine which file to download for financial statements based on timeline and period
   */
  private determineFinancialStatementPath(criteria: FinancialStatementCriteria): string {
    const { leftYear, rightYear, periodType } = criteria;

    // Check if timeline is '13 to '23
    if (leftYear === '13' && rightYear === '23') {
      return FILE_PATHS.NVIDIA_FY14;
    }

    // Check if timeline is '20 to '23 and period type is quarterly
    if (leftYear === '20' && rightYear === '23' && periodType === 'Quarterly') {
      return FILE_PATHS.CAPIQ_FY23;
    }

    // Default file for any other combination
    return FILE_PATHS.CAPIQ_DEFAULT;
  }

  /**
   * Check if two arrays are equal
   */
  private arraysEqual<T>(arr1: T[], arr2: T[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Extract filename from file path
   */
  private extractFileName(filePath: string): string {
    const parts = filePath.split('/');
    return parts[parts.length - 1] ?? 'download.xlsx';
  }

  /**
   * Create a temporary link and trigger download
   */
  private createDownloadLink(href: string, filename: string): void {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Download Tearsheet Report as RTF
   */
  public downloadTearsheetRTF(): void {
    // For now, create a placeholder RTF download
    // TODO: Generate actual RTF content from tearsheet data
    const rtfContent = this.generateTearsheetRTF();
    this.downloadRTFContent(rtfContent, 'NVIDIA-Tearsheet-Report.rtf');
  }

  /**
   * Download CIQ Report as RTF
   */
  public downloadCIQReportRTF(): void {
    // For now, create a placeholder RTF download
    // TODO: Generate actual RTF content from all page data
    const rtfContent = this.generateCIQReportRTF();
    this.downloadRTFContent(rtfContent, 'NVIDIA-CIQ-Report.rtf');
  }

  /**
   * Generate Tearsheet RTF content
   */
  private generateTearsheetRTF(): string {
    // Basic RTF structure with tearsheet data
    return `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0 Times New Roman;}}
\\f0\\fs24
{\\b NVIDIA Corporation Tearsheet Report}\\par
\\par
{\\b Company:} NVIDIA Corporation (NasdaqGS:NVDA)\\par
{\\b Website:} www.nvidia.com\\par
{\\b Employees:} 36,000\\par
{\\b Year Founded:} 1993\\par
\\par
{\\b Business Description:}\\par
NVIDIA Corporation, a computing infrastructure company, provides graphics and compute and networking solutions.\\par
\\par
{\\b Financial Information (USD, in mm):}\\par
Total Revenue: 130,497.0\\par
EBITDA: 83,317.0\\par
EBIT: 81,453.0\\par
Market Capitalization: 2,675,948.0\\par
}`;
  }

  /**
   * Generate CIQ Report RTF content
   */
  private generateCIQReportRTF(): string {
    // Basic RTF structure with all page data
    return `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0 Times New Roman;}}
\\f0\\fs24
{\\b NVIDIA Corporation - Capital IQ Report}\\par
\\par
{\\b Company Profile:}\\par
Name: NVIDIA Corporation (NasdaqGS:NVDA)\\par
Website: www.nvidia.com\\par
Employees: 36,000\\par
Ticker: NVDA (NasdaqGS)\\par
Year Founded: 1993\\par
\\par
{\\b Business Description:}\\par
NVIDIA Corporation, a computing infrastructure company, provides graphics and compute and networking solutions in the United States, Singapore, Taiwan, China, Hong Kong, and internationally.\\par
\\par
{\\b Primary Industry:} Semiconductors\\par
\\par
{\\b Office Location:}\\par
2788 San Tomas Expressway | Santa Clara, CA | 95051 | United States\\par
Phone: (408) 486-2000\\par
\\par
{\\b Financial Information (USD, in mm):}\\par
Total Revenue: 130,497.0\\par
EBITDA: 83,317.0\\par
EBIT: 81,453.0\\par
Market Capitalization: 2,675,948.0\\par
Total Enterprise Value: 2,643,008.0\\par
Cash & ST Invst.: 43,210.0\\par
TEV/Total Revenue: 20.3x\\par
TEV/EBITDA: 31.6x\\par
P/Diluted EPS Before Extra: 37.3x\\par
}`;
  }

  /**
   * Download RTF content as a file
   */
  private downloadRTFContent(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'application/rtf' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  }

  /**
   * Generate and download Quick Report (1-page summary PDF)
   */
  public downloadQuickReportPDF(data: ProfileData): void {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('NVIDIA Corporation - Quick Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Company info line
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${data.ticker} (${data.exchange}) | ${data.website}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Key metrics table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Key Metrics', 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Last Price (Delayed)', data.stockQuote.lastDelayed],
        ['Market Cap (mm)', data.stockQuote.marketCapMm],
        ['P/E Ratio', data.stockQuote.pDilutedEpsBeforeExtra],
        ['52-Week High/Low', data.stockQuote.weekHighLow52],
        ['Total Revenue', data.financialInfo.totalRevenue],
        ['EBITDA', data.financialInfo.ebitda],
        ['Net Income', data.financialInfo.netIncome],
        ['TEV/Revenue', data.financialInfo.tevTotalRevenue],
        ['TEV/EBITDA', data.financialInfo.tevEbitda],
      ],
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' },
      },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 10;

    // Company overview
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Company Overview', 14, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // Truncate description to fit on one page
    const maxDescriptionLength = 600;
    const description = data.businessDescription.length > maxDescriptionLength
      ? data.businessDescription.substring(0, maxDescriptionLength) + '...'
      : data.businessDescription;

    const splitDescription = doc.splitTextToSize(description, pageWidth - 28);
    doc.text(splitDescription, 14, yPos);
    yPos += splitDescription.length * 4 + 5;

    // Company details
    autoTable(doc, {
      startY: yPos,
      head: [['Detail', 'Information']],
      body: [
        ['Industry', data.primaryIndustry],
        ['Employees', data.numberOfEmployees],
        ['Year Founded', data.yearFounded],
        ['Headquarters', data.officeLocation],
      ],
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 'auto' },
      },
    });

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 10;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleDateString()} | LTM as of ${data.financialInfo.ltmAsOf}`, pageWidth / 2, footerY, { align: 'center' });

    // Save the PDF
    doc.save('NVIDIA-Quick-Report.pdf');
  }

  /**
   * Generate and download comprehensive Tearsheet Report PDF
   */
  public downloadTearsheetPDF(data: ProfileData): void {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace: number) => {
      if (yPos + requiredSpace > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };

    // PAGE 1: Header and Company Profile
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${data.companyName}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`${data.ticker} (${data.exchange})`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Company Information Table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Company Information', 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      head: [['Field', 'Value', 'Coverage Summary', 'Status']],
      body: [
        ['Website:', data.website, 'Coverage List:', data.coverageList],
        ['Number of Employees', data.numberOfEmployees, 'Relationships:', data.relationships],
        ['Ticker:', `${data.ticker} (${data.exchange})`, 'Projects:', data.projects],
        ['Current Professionals Profiled:', data.currentProfessionalProfiled, 'Activity Logs/Tasks:', data.activityLogsTasks],
        ['Year Founded:', data.yearFounded, '', ''],
      ],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185], fontStyle: 'bold' },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        1: { cellWidth: 45 },
        2: { fontStyle: 'bold', cellWidth: 45 },
        3: { cellWidth: 45 },
      },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 10;

    // Business Description
    checkPageBreak(30);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Business Description', 14, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const splitDescription = doc.splitTextToSize(data.businessDescription, pageWidth - 28);

    // Handle long descriptions with page breaks
    for (let i = 0; i < splitDescription.length; i++) {
      if (checkPageBreak(5)) {
        // Continue on new page
      }
      doc.text(splitDescription[i], 14, yPos);
      yPos += 4;
    }
    yPos += 5;

    // Primary Industry
    checkPageBreak(15);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Primary Industry Classification', 14, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(data.primaryIndustry, 14, yPos);
    yPos += 10;

    // Primary Office Location
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Primary Office Location', 14, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(data.officeLocation, 14, yPos);
    yPos += 4;
    doc.text(`Phone: ${data.phone}`, 14, yPos);
    yPos += 10;

    // Investors - Current and Pending
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Current and Pending Investors', 14, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const currentInvestors = doc.splitTextToSize(data.currentPendingInvestors, pageWidth - 28);
    for (let i = 0; i < currentInvestors.length; i++) {
      checkPageBreak(5);
      doc.text(currentInvestors[i], 14, yPos);
      yPos += 4;
    }
    yPos += 5;

    // Prior Investors
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Prior Investors', 14, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const priorInvestors = doc.splitTextToSize(data.priorInvestors, pageWidth - 28);
    for (let i = 0; i < priorInvestors.length; i++) {
      checkPageBreak(5);
      doc.text(priorInvestors[i], 14, yPos);
      yPos += 4;
    }
    yPos += 5;

    // Investment Arms
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Investment Arms', 14, yPos);
    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(data.investmentArms, 14, yPos);
    yPos += 10;

    // Stock Quote and Chart
    checkPageBreak(60);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Stock Quote and Chart (Currency: USD)', 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Last (Delayed)', data.stockQuote.lastDelayed],
        ['Market Cap (mm)', data.stockQuote.marketCapMm],
        ['Open', data.stockQuote.open],
        ['Shares Out. (mm)', data.stockQuote.sharesOutMm],
        ['Previous Close', data.stockQuote.previousClose],
        ['Float %', data.stockQuote.floatPercent],
        ['Change on Day', data.stockQuote.changeOnDay],
        ['Shares Sold Short (mm)', data.stockQuote.sharesSoldShortMm],
        ['Change % on Day', data.stockQuote.changePercentOnDay],
        ['Dividend Yield %', data.stockQuote.dividendYieldPercent],
        ['Day High/Low', data.stockQuote.dayHighLow],
        ['Diluted EPS Excl. Extra Items', data.stockQuote.dilutedEpsExclExtraItems],
        ['52 wk High/Low', data.stockQuote.weekHighLow52],
        ['P/Diluted EPS Before Extra', data.stockQuote.pDilutedEpsBeforeExtra],
        ['Volume (mm)', data.stockQuote.volumeMm],
        ['Avg 3M Dly Vlm (mm)', data.stockQuote.avg3MDlyVlmMm],
        ['Beta 5Y', data.stockQuote.beta5Y],
      ],
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' },
      },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 5;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(`${data.stockQuote.lastUpdated}`, 14, yPos);
    yPos += 3;
    doc.text(`${data.stockQuote.stockType}`, 14, yPos);
    yPos += 10;

    // Financial Information
    checkPageBreak(70);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Information (Currency: USD, in mm)', 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value', 'Metric', 'Value', 'Metric', 'Value']],
      body: [
        [
          'Total Revenue',
          data.financialInfo.totalRevenue,
          'Market Capitalization',
          data.financialInfo.marketCapitalization,
          'TEV/Total Revenue',
          data.financialInfo.tevTotalRevenue,
        ],
        [
          'EBITDA',
          data.financialInfo.ebitda,
          'Total Enterprise Value',
          data.financialInfo.totalEnterpriseValue,
          'TEV/EBITDA',
          data.financialInfo.tevEbitda,
        ],
        [
          'EBIT',
          data.financialInfo.ebit,
          'Cash & ST Invst.',
          data.financialInfo.cashStInvst,
          'P/Diluted EPS Before Extra',
          data.financialInfo.pDilutedEpsBeforeExtra,
        ],
        [
          'Net Income',
          data.financialInfo.netIncome,
          'Total Debt',
          data.financialInfo.totalDebt,
          'Price/Tang BV',
          data.financialInfo.priceTangBv,
        ],
        [
          'Capital Expenditure',
          data.financialInfo.capitalExpenditure,
          'Total Assets',
          data.financialInfo.totalAssets,
          'Total Debt/EBITDA',
          data.financialInfo.totalDebtEbitda,
        ],
      ],
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 32 },
        1: { cellWidth: 30 },
        2: { fontStyle: 'bold', cellWidth: 32 },
        3: { cellWidth: 30 },
        4: { fontStyle: 'bold', cellWidth: 32 },
        5: { cellWidth: 30 },
      },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 5;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    const financialNote = doc.splitTextToSize(
      `Latest Annual: ${data.financialInfo.latestAnnualDate} | Latest Interim: ${data.financialInfo.latestInterimDate}\nCurrency in USD in mm, LTM as of ${data.financialInfo.ltmAsOf}. TEV and Market Cap are calculated using a close price as of ${data.financialInfo.marketCapAsOf}`,
      pageWidth - 28
    );

    for (let i = 0; i < financialNote.length; i++) {
      checkPageBreak(5);
      doc.text(financialNote[i], 14, yPos);
      yPos += 4;
    }

    // Footer on last page
    const currentPage = (doc as { internal: { getNumberOfPages(): number } }).internal.getNumberOfPages();
    for (let i = 1; i <= currentPage; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${currentPage}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, pageHeight - 10, { align: 'right' });
    }

    // Save the PDF
    doc.save('NVIDIA-Tearsheet-Report.pdf');
  }
}
